import React, { useState } from "react";
import supabase from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import "./Auth.css";

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    address: "",
    openTime: "",
    closeTime: "",
  });

  const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const toggleForm = () => {
    setIsLogin(!isLogin);
    setError("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const { error: authError } = await supabase.auth.signInWithPassword({
      email: loginData.email,
      password: loginData.password,
    });

    setLoading(false);
    if (authError) {
      setError(authError.message);
    } else {
      navigate("/dashboard");
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const { email, password, name, phone, address, openTime, closeTime } =
      signupData;

    // 1. Geocode first
    let lat = null;
    let lng = null;

    try {
      const geoRes = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address
        )}&key=${GOOGLE_API_KEY}`
      );
      const geoData = await geoRes.json();
      if (geoData.status === "OK") {
        lat = geoData.results[0].geometry.location.lat;
        lng = geoData.results[0].geometry.location.lng;
      } else {
        console.warn("Geocoding failed:", geoData.status);
      }
    } catch (geoError) {
      console.error("Error fetching geocode:", geoError);
    }

    // 2. Then call signup
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          role: "car_wash_staff",
          name,
          phone,
          address,
          open_time: openTime,
          close_time: closeTime,
          lat,
          long: lng,
        },
      },
    });

    const user = data?.user;

    if (signUpError || !user) {
      setError(signUpError?.message || "Signup failed");
      setLoading(false);
      return;
    }

    // ✅ Call Edge Function to send verification email
    try {
      const response = await fetch(
        "https://ddzzlkrhtxznjxjddini.supabase.co/functions/v1/send-verification-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: user.email,
            user_id: user.id,
          }),
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        console.warn("Edge function error:", errText);
      }
    } catch (err) {
      console.error("Function invoke failed:", err);
    }

    alert(
      "✅ Signup successful! Please check your email and confirm your address to activate your account."
    );

    setIsLogin(true);
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>{isLogin ? "Welcome Back" : "Create Account"}</h2>
          <p>
            {isLogin
              ? "Sign in to manage your car wash"
              : "Register your car wash business"}
          </p>
        </div>

        {error && <div className="auth-error">{error}</div>}

        {isLogin ? (
          <form onSubmit={handleLogin} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={loginData.email}
                onChange={(e) =>
                  setLoginData({ ...loginData, email: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={loginData.password}
                onChange={(e) =>
                  setLoginData({ ...loginData, password: e.target.value })
                }
                required
              />
            </div>

            <button
              type="submit"
              className="auth-button primary"
              disabled={loading}
            >
              {loading ? "Signing In..." : "Sign In"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSignup} className="auth-form">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={signupData.email}
                onChange={(e) =>
                  setSignupData({ ...signupData, email: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={signupData.password}
                onChange={(e) =>
                  setSignupData({ ...signupData, password: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Business Name</label>
              <input
                type="text"
                value={signupData.name}
                onChange={(e) =>
                  setSignupData({ ...signupData, name: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                value={signupData.phone}
                onChange={(e) =>
                  setSignupData({ ...signupData, phone: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Address</label>
              <input
                type="text"
                value={signupData.address}
                onChange={(e) =>
                  setSignupData({ ...signupData, address: e.target.value })
                }
                required
              />
            </div>

            <div className="time-inputs">
              <div className="form-group">
                <label>Open Time</label>
                <input
                  type="time"
                  value={signupData.openTime}
                  onChange={(e) =>
                    setSignupData({ ...signupData, openTime: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Close Time</label>
                <input
                  type="time"
                  value={signupData.closeTime}
                  onChange={(e) =>
                    setSignupData({ ...signupData, closeTime: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="auth-button primary"
              disabled={loading}
            >
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          </form>
        )}

        <div className="auth-toggle">
          <button onClick={toggleForm} className="toggle-button">
            {isLogin
              ? "Don't have an account? Sign up"
              : "Already have an account? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
